/**
 * Пространственный хэш (ТЗ §11).
 *
 * Наивный перебор при 200 шарах и 400 снарядах даёт 80 000 проверок за тик.
 * Сетка 128 px сводит это к соседним ячейкам. Хэш перестраивается каждый тик —
 * при таком числе объектов это дешевле, чем поддерживать инкрементально.
 */

export interface HasPos {
  x: number;
  y: number;
}

export class SpatialHash<T extends HasPos> {
  private readonly cell: number;
  private readonly buckets = new Map<number, T[]>();
  /** Пул массивов: не аллоцируем заново каждый кадр. */
  private readonly pool: T[][] = [];
  private used: T[][] = [];

  constructor(cellSize = 128) {
    this.cell = cellSize;
  }

  private key(cx: number, cy: number): number {
    // Смещение, чтобы отрицательные координаты не схлопывались с положительными.
    return (cx + 4096) * 8192 + (cy + 4096);
  }

  clear(): void {
    for (const arr of this.used) {
      arr.length = 0;
      this.pool.push(arr);
    }
    this.used.length = 0;
    this.buckets.clear();
  }

  insert(item: T): void {
    const cx = Math.floor(item.x / this.cell);
    const cy = Math.floor(item.y / this.cell);
    const k = this.key(cx, cy);
    let b = this.buckets.get(k);
    if (!b) {
      b = this.pool.pop() ?? [];
      this.buckets.set(k, b);
      this.used.push(b);
    }
    b.push(item);
  }

  /** Кандидаты в радиусе r вокруг точки. Возвращает содержимое ячеек, без точной фильтрации. */
  query(x: number, y: number, r: number, out: T[]): T[] {
    out.length = 0;
    const c = this.cell;
    const x0 = Math.floor((x - r) / c);
    const x1 = Math.floor((x + r) / c);
    const y0 = Math.floor((y - r) / c);
    const y1 = Math.floor((y + r) / c);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const b = this.buckets.get(this.key(cx, cy));
        if (!b) continue;
        for (let i = 0; i < b.length; i++) out.push(b[i]!);
      }
    }
    return out;
  }
}
